<template>
  <div class="metric">
    <SnapQLDebugButton
      class="kpi-debug-button"
      :dashboard-module="dashboardModule"
      :dashboard-data-context="dashboardDataContext"
      :dashboard-key="dashboardKey" />
    <div
      v-if="queryError"
      class="query-error">
      <b-icon
        icon="alert-circle-outline"
        size="is-small" />
      <span>{{ queryError }}</span>
    </div>
    <div
      v-else-if="!isLoading"
      class="row main-row">
      <div class="left main-title">
        {{ data.primary_row.label }}
      </div>

      <div class="right main-value-container">
        <div
          class="main-value"
          :class="{
            red: data.primary_row.conditional_quality === 'bad',
            green: data.primary_row.conditional_quality === 'good',
            'text-small': useSmallerFont,
          }">
          <i
            v-if="data.primary_row.conditional_direction !== 'none'"
            class="material-icons arrow-icon">
            {{
              data.primary_row.conditional_direction === "up"
                ? "arrow_upward"
                : "arrow_downward"
            }}
          </i>
          {{ data.primary_row?.value }}
        </div>
      </div>
    </div>

    <b-skeleton
      v-else
      height="62"
      style="border-radius: 0"
      width="100%"
      :active="isLoading" />

    <div v-if="!isLoading && data?.secondary_rows">
      <div
        v-for="subRow in data?.secondary_rows"
        :key="subRow.id"
        class="row sub-row">
        <div class="left sub-title">
          {{ subRow.label }}
        </div>

        <div
          class="right sub-value"
          :class="{
            red: subRow.conditional_quality === 'bad',
            green: subRow.conditional_quality === 'good',
          }">
          <i
            v-if="subRow.conditional_direction !== 'none'"
            class="material-icons arrow-icon">
            {{
              subRow.conditional_direction === "up"
                ? "arrow_upward"
                : "arrow_downward"
            }}
          </i>
          {{ subRow.value }}
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import axios from 'axios'
  import SnapQLDebugButton from '../SnapQLDebugButton.vue'

  export default {
    name: 'KPIModule',
    components: { SnapQLDebugButton, },
    props: {
      isReady: {
        type: Boolean,
        required: true,
      },
      dashboardModule: {
        type: Object,
        required: true,
      },
      dashboardDataContext: {
        type: Object,
        required: true,
      },
      dashboardKey: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        data: null,
        isLoading: true,
        queryError: null,
        cancelTokenSource: null,
      }
    },
    computed: {
      moduleDataContext () {
        if (
          !this.dashboardModule.runtime_measure_control ||
          !this.dashboardModule.runtime_measure_option
        ) {
          return this.dashboardDataContext
        }

        return {
          ...this.dashboardDataContext,
          control_selections: {
            ...this.dashboardDataContext.control_selections,
            [this.dashboardModule.runtime_measure_control]: {
              ...this.dashboardDataContext.control_selections[
                this.dashboardModule.runtime_measure_control
              ],
              selected_options: [this.dashboardModule.runtime_measure_option,],
            },
          },
        }
      },

      useSmallerFont () {
        return this.data?.primary_row?.value?.length > 12
      },

    },

    watch: {
      dashboardDataContext: {
        handler () {
          this.loadData()
        },
        deep: true,
      },
    },
    mounted () {
      this.loadData()
    },
    beforeDestroy () {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Component destroyed')
      }
      if (this.abortController) {
        this.abortController.abort()
      }
    },
    methods: {
      async loadData () {
        if (!this.isReady) {
          return
        }
        const useSnapQL = this.$hasFeatureFlag('USE_SNAPQL')
        if (useSnapQL) {
          return this.loadDataStreaming()
        }
        try {
          // Cancel previous request if it exists
          if (this.cancelTokenSource) {
            this.cancelTokenSource.cancel('New request initiated')
          }

          // Create new CancelToken
          this.cancelTokenSource = axios.CancelToken.source()

          this.isLoading = true
          this.queryError = null

          const response = await this.$apis.companyConfigs.getDashboardModuleData(
            this.dashboardKey,
            this.dashboardModule.key,
            this.moduleDataContext,
            { cancelToken: this.cancelTokenSource.token, }
          )

          if (response?.error) {
            if (response?.code !== 'ERR_CANCELED') {
              this.$buefy.toast.open({
                message: 'Failed to load KPI Data',
                type: 'is-warning',
              })
            }
            return
          }

          this.data = response
          this.isLoading = false
        } catch (error) {
          console.error('Error loading data:', error)
        }
      },
      async loadDataStreaming () {
        if (this.abortController) {
          this.abortController.abort()
        }
        this.abortController = new AbortController()
        this.isLoading = true
        this.queryError = null

        await this.$apis.companyConfigs.streamDashboardModuleData(
          this.dashboardKey,
          this.dashboardModule.key,
          this.moduleDataContext,
          {
            data: (payload) => {
              this.data = payload
            },
            complete: () => {
              this.isLoading = false
            },
            truncated: (payload) => {
              this.$buefy.toast.open({
                message: payload.message,
                type: 'is-warning',
                duration: 8000,
              })
              this.isLoading = false
            },
            error: (err) => {
              this.queryError = err.message || 'Failed to load KPI data'
              this.isLoading = false
            },
          },
          this.abortController.signal
        )
      },
    },
  }
</script>
<style scoped lang="scss">
.query-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 16px;
  color: #b5451b;
  font-size: 13px;
}
.metric {
  position: relative;
  background-color: white;
  border: 1px solid #e9e9ea;
  border-radius: 4px;
  overflow: hidden;

  .row {
    display: flex;

    &.main-row {
      color: $ui-01;
    }

    &.sub-row {
      // background-color: $ui-08;
       border-top: 1px solid #e9e9ea;
      color: $ui-03;
    }

    &:last-child {
      border-bottom: none;
    }

    .left {
      padding: 12px 16px;
      border-right: 1px solid #e9e9ea;
      text-align: left;
      width: 55%;
      font-size: 14px;
    }

    .right {
      width: 45%;
      font-size: 16px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      font-family: "Inconsolata", monospace;
      font-weight: 600 !important;
    }

    .main-title {
      font-size: 16px;
      font-weight: 500;
      height: 62px;
      padding: 16px;
      display: flex;
      align-items: center;
    }

    .main-value-container {
      font-size: 20px;
      font-weight: 500;
      height: 62px;
      display: flex;
      justify-content: flex-end;
      align-items: center;
      overflow: hidden;
      .main-value {
        padding: 0 16px;
        display: flex;
        width: 100%;
        height: 100%;
        overflow: hidden;
        align-items: center;
        justify-content: flex-end;

        &.text-small {
          @include mobile {
            font-size: 16px;
          }
        }
        .arrow-icon {
          font-size: 18px;

          margin-right: auto;
        }
      }
    }

    .sub-value {
      padding: 12px 16px;

      .arrow-icon {
        font-size: 18px;

        margin-right: auto;
      }
    }

    .green {
      color: #008c84;
      &.sub-value {
        background-color: rgba(0, 140, 132, 0.05);
      }
    }
    .red {
      color: #d31b5e;
      &.sub-value {
        background-color: rgba(211, 27, 94, 0.05);
      }
    }
  }
}

.kpi-debug-button {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.15s;

  .metric:hover & {
    opacity: 1;
  }
}
</style>
